<template>
  <div v-if="entry" class="live">
    <div class="liveScroll">
      <!-- WHY -->
      <WhyNowCard :label="entry.label" :why-now="entry.why_now" />

      <!-- WHO -->
      <ContactIdentity :entry="entry" />

      <!-- CALL SURFACE: the live state rides on the row being dialled -->
      <PhoneLines
        :entry="entry"
        :session-lines="sessionLines"
        :active-e164="activeE164"
        :ringing="ringing"
        :connected="connected"
        :duration-seconds="durationSeconds"
        :from-e164="fromE164"
        :media-fault="mediaFault"
        :machine-detected="machineDetected"
        :checking-line="checkingLine"
      />

      <!-- BACKGROUND -->
      <AboutBriefs :entry="entry" :group-color="groupColor" />

      <VoicemailCard :voicemail="entry.voicemail" />

      <!-- notes -->
      <div class="notesCard surface-card">
        <div class="notesHead">
          <span class="notesLabel"><AppIcon name="pencil" :size="13" />Notes</span>
        </div>
        <textarea
          ref="notesEl"
          class="notesInput"
          :value="notes"
          placeholder="Jot context, objections, next steps…"
          @input="onNotesInput"
        ></textarea>
      </div>

      <!-- In flow under Notes while the column has room; pinned to the
           column's bottom edge once it doesn't. -->
      <div class="dock sticky-dock">
        <div v-if="mediaNotice" class="mediaNotice" role="status">
          <AppIcon name="about" :size="13" />
          <span>{{ mediaNotice.text }}</span>
          <button
            v-if="mediaNotice.action === 'redial'"
            type="button"
            class="noticeLink"
            @click="$emit('redial')"
          >
            Redial
          </button>
        </div>

        <div class="controls surface-card">
          <!-- Two clusters: call controls, then the outcomes with Hang up. -->
          <div class="controlGroup">
            <div ref="keypadWrap" class="keypadWrap">
              <button
                class="keypadBtn"
                type="button"
                :aria-expanded="keypadOpen"
                aria-haspopup="dialog"
                :disabled="!connected"
                :title="connected ? 'Keypad' : 'Available once someone answers'"
                @click="keypadOpen = !keypadOpen"
              >
                <AppIcon name="keypad" :size="14" />Keypad
              </button>
              <DialerKeypad
                v-if="keypadOpen"
                ref="keypadRef"
                class="keypadPanel"
                :connected="connected"
                :sent-sequences="sentSequences ?? []"
                @send="$emit('sendDigits', $event)"
              />
            </div>
          </div>
          <div class="controlGroup">
            <div class="quickPill" role="group" aria-label="Log an outcome and continue">
              <button class="quickSeg" type="button" :disabled="checkingLine" @click="$emit('quickDispose', 'no_answer')">
                No answer
              </button>
              <button class="quickSeg" type="button" :disabled="checkingLine" @click="$emit('quickDispose', 'gatekeeper')">
                Gatekeeper
              </button>
              <button
                class="quickSeg"
                type="button"
                aria-label="Skip — no outcome logged"
                :disabled="checkingLine"
                @click="$emit('skip')"
              >
                Skip<AppIcon name="arrow_right" :size="14" />
              </button>
            </div>
            <button class="btn danger" type="button" :disabled="checkingLine" @click="$emit('hangup')">
              <AppIcon name="phone_off" :size="14" />Hang up
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import ContactIdentity from './ContactIdentity.vue'
import PhoneLines from './PhoneLines.vue'
import WhyNowCard from './WhyNowCard.vue'
import AboutBriefs from './AboutBriefs.vue'
import VoicemailCard from './VoicemailCard.vue'
import DialerKeypad from './DialerKeypad.vue'
import { useClickOutside } from '../../composables/useClickOutside'
import { isTypingTarget } from '../../composables/useHotkeys'
import type { CallerQueueEntry } from '../../../services/dialerApi'
import type { SessionLine } from '../../composables/useDialerSession'
import type { MediaFault } from '../../../composables/useDialer'
import type { MediaNotice } from './mediaFaults'

const props = defineProps<{
  entry: CallerQueueEntry | null
  fromE164: string | null
  durationSeconds: number
  ringing: boolean
  connected: boolean
  notes: string
  groupColor: string
  /** The line currently ringing. */
  activeE164?: string
  /** This person's session lines, with outcomes already logged on them. */
  sessionLines?: readonly SessionLine[]
  /** AMD called this line a machine — advisory badge, no action attached. */
  machineDetected?: boolean
  /** The call ended on its own and the session is reading Twilio's verdict on the line. */
  checkingLine?: boolean
  /** Live media health — amber-flags the pill on the dialled row. */
  mediaFault?: MediaFault | null
  /** The louder surface for a call-killing fault; null leaves the pill alone. */
  mediaNotice?: MediaNotice | null
  /** Keypad sequences already sent on this call, for the panel's readout. */
  sentSequences?: readonly string[]
}>()
const emit = defineEmits<{
  (e: 'update:notes', value: string): void
  (e: 'skip'): void
  (e: 'quickDispose', code: 'no_answer' | 'gatekeeper'): void
  (e: 'hangup'): void
  (e: 'redial'): void
  (e: 'sendDigits', seq: string): void
}>()

const notesEl = ref<HTMLTextAreaElement | null>(null)
const keypadWrap = ref<HTMLElement | null>(null)
const keypadRef = ref<InstanceType<typeof DialerKeypad> | null>(null)
const keypadOpen = ref(false)
useClickOutside(keypadWrap, keypadOpen)

// Notes owns the keyboard on a live call — but never at the expense of a control
// the rep is already driving with it elsewhere (a Phone settings field or
// dropdown, the rail's search).
const _KEYBOARD_WIDGETS =
  'select, [role="listbox"], [role="combobox"], [role="menu"], [role="menuitem"], [role="option"]'

function focusNotes(): void {
  const active = document.activeElement
  if (
    active instanceof HTMLElement &&
    !keypadWrap.value?.contains(active) &&
    (isTypingTarget(active) || active.matches(_KEYBOARD_WIDGETS))
  ) {
    return
  }
  notesEl.value?.focus({ preventScroll: true })
}

// The connect-time focus deliberately doesn't scroll (WHY/WHO stay in view), so
// the first keystroke brings a below-the-fold Notes into view instead.
function onNotesInput(e: Event): void {
  const el = e.target as HTMLTextAreaElement
  emit('update:notes', el.value)
  el.scrollIntoView?.({ block: 'nearest' }) // absent in jsdom
}

watch(
  () => props.connected,
  (c) => {
    if (c) focusNotes()
  },
)
onMounted(() => {
  if (props.connected) focusNotes()
})
watch(keypadOpen, (open) => {
  if (open) void nextTick(() => keypadRef.value?.focus())
  else focusNotes()
})
</script>

<style scoped>
.live { display: flex; flex-direction: column; flex: 1; min-height: 0; position: relative; }
/* scroll-padding keeps a scrolled-to Notes clear of the pinned dock (strip + notice). */
.liveScroll { flex: 1; min-height: 0; overflow-y: auto; scroll-padding-bottom: calc(120px + var(--space-5)); padding: var(--space-5) var(--space-7); display: flex; flex-direction: column; gap: var(--space-4); }

/* The strip widens with the column up to --dock-w, never to the card's edges;
   the dock's own box is inert so it never swallows clicks beside the strip. */
.dock { --dock-w: 760px; display: flex; flex-direction: column; align-items: center; gap: var(--space-2); pointer-events: none; }
.dock > * { width: min(100%, var(--dock-w)); pointer-events: auto; }
.mediaNotice { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); border-radius: var(--radius-lg); font-size: var(--fs-sm); line-height: var(--lh-normal); background: var(--hue-amber-soft); border: 1px solid var(--hue-amber-edge); color: var(--hue-amber); }
.noticeLink { margin-left: auto; border: 0; background: transparent; padding: 0; font: inherit; font-weight: var(--fw-semibold); color: inherit; cursor: pointer; text-decoration: underline; }

.notesCard { padding: var(--space-4); }
.notesHead { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: var(--space-2); }
.notesLabel { display: inline-flex; align-items: center; gap: 6px; font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.notesInput { width: 100%; min-height: 72px; resize: vertical; border: 0; background: transparent; font-family: var(--font-sans); font-size: var(--fs-base); line-height: var(--lh-loose); color: var(--color-text-primary); outline: none; }
.notesInput::placeholder { color: var(--color-text-tertiary); }

/* Descendant selector: outranks the surface-card utility's radius/shadow whatever the sheet order. */
.dock .controls { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: var(--space-3) var(--space-4); border-radius: var(--radius-xl); box-shadow: var(--shadow-lg); }
.controlGroup { display: flex; align-items: center; gap: var(--space-3); }

.keypadWrap { position: relative; display: inline-flex; }
.keypadBtn { display: inline-flex; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-4); border: 0; border-radius: var(--radius-full); background: var(--color-bg-app); box-shadow: var(--shadow-neu-in-cream); cursor: pointer; font-family: var(--font-sans); font-size: var(--fs-sm); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-tight); line-height: 1; color: var(--color-text-secondary); transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out); }
.keypadBtn:hover:not(:disabled), .keypadBtn[aria-expanded="true"] { background: var(--color-bg-subtle); color: var(--color-text-primary); }
.keypadBtn:active:not(:disabled) { transform: scale(0.97); }
.keypadBtn:disabled { opacity: 0.5; cursor: not-allowed; }
.keypadPanel { position: absolute; bottom: calc(100% + 6px); left: 0; z-index: 40; }

/* One recessed pill split into three tap zones: the two most common outcomes
   plus Skip, so the usual call ends without opening the disposition screen. */
.quickPill { display: inline-flex; align-items: stretch; border-radius: var(--radius-full); background: var(--color-bg-app); box-shadow: var(--shadow-neu-in-cream); overflow: hidden; }
.quickSeg { display: inline-flex; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-4); border: 0; background: transparent; cursor: pointer; font-family: var(--font-sans); font-size: var(--fs-sm); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-tight); line-height: 1; color: var(--color-text-secondary); transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out); }
.quickSeg + .quickSeg { border-left: 1px solid var(--color-border-subtle); }
.quickSeg:hover:not(:disabled) { background: var(--color-bg-subtle); color: var(--color-text-primary); }
.quickSeg:active:not(:disabled) { transform: scale(0.97); }
.quickSeg:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
